<script setup lang="ts">
  import { computed } from 'vue'
  import StarRatingDisplay from '~renderer/components/StarRatingDisplay.vue'
  import { typeColors } from '~renderer/util/island'
  import { IslandPublicView } from '~shared/types/island'

  const props = defineProps<{
    island: IslandPublicView
  }>()

  const emit = defineEmits<{
    report: [visitId: string]
  }>()

  const rating = computed(() => props.island.averageRating ?? 0)
  const fruitName = computed(() => titleTextOf(props.island.fruit || ''))
  const npcIcons = computed(() => (props.island.npc || []).map(iconOf))

  const iconOf = (
    item: NonNullable<NonNullable<typeof props.island>['npc']>[number],
  ): { path: string; alt: string } => {
    const npcImageName = item.replace(/ /g, '-').replaceAll('.', '').toLowerCase()

    return { path: `/images/npc/${npcImageName}.svg`, alt: item }
  }

  const titleTextOf = (str: string): string =>
    `${str.slice(0, 1).toUpperCase()}${str.slice(1).toLowerCase()}`

  const entryFeeText = computed(() => {
    if (!props.island.entryFee?.length) return

    const bellsFeeItem = props.island.entryFee.find((fee) => fee[0]?.type === 'bells')?.[0]
    const bellsFee = bellsFeeItem && 'amount' in bellsFeeItem ? bellsFeeItem.amount : 0

    const nmtFeeItem = props.island.entryFee.find((fee) => fee[0]?.type === 'nmt')?.[0]
    const nmtFee = nmtFeeItem && 'amount' in nmtFeeItem ? nmtFeeItem.amount : 0

    if (!bellsFee && !nmtFee) return 'No Entry Fee'

    const bellsText = `<img class="size-5" alt "Bells" src="/images/icons/bells.svg"><span class="text-tan-300"> ${bellsFee}</span>`
    const nmtText = `<img class="size-5" alt="NMT" src="/images/icons/nmt.svg"><span class="text-tan-300"> ${nmtFee}</span>`
    const orText = bellsText && nmtText ? '<span> or </span>' : ''

    return `${bellsText}${orText}${nmtText}<span class="hidden lg:inline"> to Enter</span>`
  })
</script>

<template>
  <a
    :href="`/island/${island.turnipCode}`"
    class="group border-base-tan relative flex cursor-pointer flex-col rounded-2xl border bg-[#2d262f] drop-shadow-xl transition-transform duration-200 hover:-translate-y-1"
    data-prefetch-static-assets="false"
  >
    <div
      class="bg-tan-800 grid w-full grid-cols-[1.5rem_1fr_4rem] gap-2 rounded-t-2xl px-3 py-2 text-white"
    >
      <i-fa7-solid-house class="size-6" />
      <div class="truncate font-medium">
        <span>{{ island.name }}</span>
      </div>
      <div class="flex flex-row items-center justify-end">
        <StarRatingDisplay :rating="rating" :size="5" :padding="0" />
      </div>
    </div>
    <div class="grid w-full grid-cols-[1fr_7rem] gap-2 px-3 py-2 text-white">
      <div class="flex flex-row items-center gap-3">
        <div
          class="truncate rounded-md px-1.5 py-0.5 text-xs font-semibold"
          :class="typeColors[island.type]"
        >
          {{ island.type.toUpperCase() }}
        </div>
        <div
          class="truncate rounded-md px-1.5 py-0.5 text-xs font-semibold"
          :class="typeColors[island.type]"
        >
          {{ island.hemisphere === 'north' ? 'N' : 'S' }}
        </div>

        <div class="hidden items-center gap-1 truncate lg:flex">
          <img :src="`/images/icons/${island.fruit}.svg`" :alt="fruitName" class="size-5" />
          <img
            v-for="icon in npcIcons"
            :key="`${icon.alt}:${icon.path}`"
            :src="icon.path"
            :alt="icon.alt"
            class="size-5"
          />
        </div>
      </div>
      <div class="flex flex-row items-center justify-end gap-2">
        <div v-if="island.turnipPrice" class="flex items-center gap-1">
          <img src="/images/icons/turnip.svg" alt="Turnip" class="size-5" />
          <span class="text-sm font-medium text-white">{{ island.turnipPrice }} Bells</span>
        </div>
      </div>
    </div>
    <div class="flex items-start justify-start gap-1 truncate px-3 py-2 lg:hidden">
      <img :src="`/images/icons/${island.fruit}.svg`" :alt="fruitName" class="size-5" />
      <img
        v-for="icon in npcIcons"
        :key="`${icon.alt}:${icon.path}`"
        :src="icon.path"
        :alt="icon.alt"
        class="size-5"
      />
    </div>
    <div class="grid w-full grid-cols-[1fr_6rem] gap-2 px-3 py-2 text-white">
      <div
        v-html="entryFeeText"
        class="flex flex-row items-center gap-1 align-middle text-sm font-medium"
      ></div>

      <div class="text-base-tan flex items-center justify-end gap-3 text-sm font-bold">
        <!-- Visitors -->
        <div class="flex items-center gap-1">
          <i-te-crowd class="size-5" />
          <span class="text-trim-end">{{ island.currentVisitorCount }}</span>
        </div>

        <!-- Queue -->
        <div class="flex items-center gap-1">
          <i-fa7-solid-hourglass-start class="size-5" />
          <span
            ><span class="text-white">{{ island.currentWaitingCount }}</span
            >/{{ island.queueLimit }}</span
          >
        </div>
      </div>
    </div>
  </a>
</template>
